'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { useI18n } from '~/features/i18n';
import { cn } from '~/utils';
import { useActivityAiChatStore } from '../activity-ai-chat-store';
import { useDeleteAiSearchChat } from '../useAiChatHistory';
import { RecentChatsList } from '../RecentChatsList';
import { AI_SEARCH_EVENTS, useAiSearchAnalytics } from '../ai-search-analytics';
export const ActivityAiHistoryHeader: React.FC<{
  onClose: () => void;
  onCloseSheet?: () => void;
  className?: string;
}> = t0 => {
  const $ = _c(24);
  const {
    onClose,
    onCloseSheet,
    className
  } = t0;
  const i18n = useI18n();
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative flex shrink-0 items-center gap-2 px-6 pb-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== i18n) {
    t2 = i18n._("activity.search.ai.backToChat", undefined, {
      message: "Back to AI search"
    });
    $[2] = i18n;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowLeft className="size-4" strokeWidth={2.25} aria-hidden={true} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== i18n) {
    t4 = i18n._("activity.search.ai.back", undefined, {
      message: "Back"
    });
    $[5] = i18n;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onClose || $[8] !== t2 || $[9] !== t4) {
    t5 = <button type="button" onClick={onClose} aria-label={t2} className="-ml-2 inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-sm font-medium text-blue-900/80 transition-colors hover:bg-blue-50 hover:text-blue-900">{t3}{t4}</button>;
    $[7] = onClose;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== i18n) {
    t6 = i18n._("activity.search.ai.recentChats", undefined, {
      message: "Recent chats"
    });
    $[11] = i18n;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <p className="pointer-events-none absolute inset-x-0 flex h-7 items-center justify-center truncate text-center text-base font-semibold leading-tight text-blue-900">{t6}</p>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="min-w-0 flex-1" aria-hidden={true} />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== i18n || $[17] !== onCloseSheet) {
    t9 = onCloseSheet ? <button type="button" onClick={onCloseSheet} aria-label={i18n._("activity.search.ai.close", undefined, {
      message: "Close"
    })} className="-mr-1 shrink-0 rounded-full p-1 text-blue-400 transition-colors hover:bg-blue-50 hover:text-blue-900"><X className="size-4" strokeWidth={2.25} aria-hidden={true} /></button> : null;
    $[16] = i18n;
    $[17] = onCloseSheet;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t1 || $[20] !== t5 || $[21] !== t7 || $[22] !== t9) {
    t10 = <div className={t1}>{t5}{t7}{t8}{t9}</div>;
    $[19] = t1;
    $[20] = t5;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};

/**
 * In-panel drawer listing the user's chat threads (seeded from Postgres into
 * `useActivityAiChatStore`). Lets the user switch between threads, start a new
 * chat, and delete threads (delete cascades server-side via `aiChat.delete`).
 */
export const ActivityAiHistoryDrawer: React.FC<{
  open: boolean;
  activeThreadId: string;
  onClose: () => void;
  onSelectThread: (id: string) => void;
  /** Closes the whole AI sheet (mobile). When set, a ✕ renders on the header's right. */
  onCloseSheet?: () => void;
  /** Parent already rendered the back/title/close header in the shared header slot. */
  hideHeader?: boolean;
}> = t0 => {
  const $ = _c(17);
  const {
    open,
    activeThreadId,
    onClose,
    onSelectThread,
    onCloseSheet,
    hideHeader: t1
  } = t0;
  const hideHeader = t1 === undefined ? false : t1;
  const i18n = useI18n();
  const analytics = useAiSearchAnalytics();
  const threads = useActivityAiChatStore(_temp);
  const deleteChat = useDeleteAiSearchChat();
  let t2;
  if ($[0] !== threads) {
    t2 = Object.values(threads).sort(_temp2);
    $[0] = threads;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sortedThreads = t2;
  if (!open) {
    return null;
  }
  let t3;
  if ($[2] !== hideHeader || $[3] !== onClose || $[4] !== onCloseSheet) {
    t3 = hideHeader ? null : <ActivityAiHistoryHeader onClose={onClose} onCloseSheet={onCloseSheet} />;
    $[2] = hideHeader;
    $[3] = onClose;
    $[4] = onCloseSheet;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== activeThreadId || $[7] !== analytics || $[8] !== deleteChat || $[9] !== i18n || $[10] !== onClose || $[11] !== onSelectThread || $[12] !== sortedThreads) {
    t4 = <div className="flex-1 overflow-y-auto px-6 py-4">{sortedThreads.length === 0 ? <p className="rounded-2xl border border-dashed border-blue-900/15 bg-stone-50 px-3 py-4 text-center text-xs text-blue-900/60">{i18n._("activity.search.ai.historyHint", undefined, {
          message: "Your searches will appear here."
        })}</p> : <RecentChatsList threads={sortedThreads} activeThreadId={activeThreadId} showSearch={true} variant="drawer" onSelect={id => {
        analytics[AI_SEARCH_EVENTS.CHAT_RESUMED]({
          session_id: id,
          source: "history_drawer"
        });
        onSelectThread(id);
        onClose();
      }} onDelete={id_0 => deleteChat(id_0)} />}</div>;
    $[6] = activeThreadId;
    $[7] = analytics;
    $[8] = deleteChat;
    $[9] = i18n;
    $[10] = onClose;
    $[11] = onSelectThread;
    $[12] = sortedThreads;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <div className="absolute inset-0 z-20 flex flex-col bg-white">{t3}{t4}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
function _temp(s) {
  return s.threads;
}
function _temp2(a, b) {
  return b.createdAt - a.createdAt;
}